import React, { memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { MachineIcon } from './MachineIcon';
import { type Machine } from '../services/BackendDataService';
import { translateStatus, getMachineTypeCategory } from '../utils/sapMetadataTranslations';
import { useThemeClasses } from '../providers/ThemeProvider';
import { useSAPData } from '../hooks/useSAPData';
import { useSAPDataStore } from '../store/SAPDataStore';

// Simple cx utility for combining class names
const cx = (...classes: (string | undefined | null | false)[]): string => {
  return classes.filter(Boolean).join(' ');
};

interface MachineCardProps {
  machine: Machine;
  onClick: (machine: Machine) => void;
  className?: string;
}

export const MachineCard: React.FC<MachineCardProps> = memo(({ 
  machine, 
  onClick, 
  className = "" 
}) => {
  const { t } = useTranslation();
  const themeClasses = useThemeClasses();
  const { lines } = useSAPData();
  const showOnlyWithData = useSAPDataStore(state => state.showOnlyWithData);

  // Look up line name and number from lineId
  const lineInfo = useMemo(() => {
    if (!machine.lineId) return { lineName: null, lineNumber: null };
    const line = lines.find(line => 
      line.linieBK === machine.lineId || 
      line.lineId === machine.lineId
    );
    return { lineName: line?.linieName, lineNumber: line?.linieNummer };
  }, [machine.lineId, lines]);
  
  const { lineName, lineNumber } = lineInfo;

  // Memoize the click handler to prevent unnecessary re-renders
  const handleClick = useCallback(() => {
    onClick(machine);
  }, [onClick, machine]);

  return (
    <div
      className={cx(
        themeClasses.cardBg,
        themeClasses.cardBorder,
        themeClasses.cardShadow,
        "relative p-4 rounded-lg border cursor-pointer transition-all duration-200 hover:shadow-lg transform hover:-translate-y-1 hover:rotate-1 overflow-hidden",
        className
      )}
      onClick={handleClick}
      style={{perspective: '800px'}}
    >
      {/* Machine Icon - Top Right Corner */}
      <div className="absolute top-0 right-0 pointer-events-none">
        <MachineIcon 
          machineId={machine.maschineBK || machine.maschinennummer}
          machineType={machine.bautyp || machine.machineType}
          size={140}
          className={themeClasses.textMuted}
        />
      </div>
      
      {/* Machine Info - Left side with padding for icon */}
      <div className="relative z-10 pr-32">
        <div className="flex items-start space-x-2 mb-3">
          <div className={cx(
            "w-3 h-3 rounded-full flex-shrink-0 mt-1",
            machine.datenVerfuegbar 
              ? themeClasses.bgSuccess
              : themeClasses.bgError
          )} />
          <h5 className={cx(themeClasses.textSecondary, "text-lg font-semibold leading-tight")}>
            {machine.maschinennummer} - {(machine.bautyp || machine.machineType || '').replace(new RegExp(`^${machine.maschinennummer}\\s*[-\u2013]\\s*`), '').replace(new RegExp(`\\s*\\(${machine.maschinennummer}\\)$`), '') || 'Unknown'}
          </h5>
        </div>
      </div>
      
      {/* Machine Details */}
      <div className="space-y-2 text-sm">
        <div className="flex items-center space-x-2">
          <span className={cx(themeClasses.textSecondary, "font-medium")}>{t('type') || 'Type'}:</span> 
          <span className={cx(themeClasses.textSecondary)}>{t(`machineTypeCategories.${getMachineTypeCategory(machine.bautyp || machine.machineType || '')}`, getMachineTypeCategory(machine.bautyp || machine.machineType || ''))}</span>
        </div>
        <div className="flex items-center space-x-2">
          <span className={cx(themeClasses.textSecondary, "font-medium")}>{t('line') || 'Line'}:</span> 
          <span className={cx(themeClasses.textSecondary)}>{lineNumber && lineName ? `${lineNumber} - ${lineName}` : (lineNumber || lineName || 'N/A')}</span>
        </div>
        {machine.customerName && (
          <div className="flex items-center space-x-2">
            <span className={cx(themeClasses.textSecondary, "font-medium")}>{t('customer') || 'Customer'}:</span> 
            <span className={cx(themeClasses.textSecondary)}>{machine.customerName}</span>
          </div>
        )}
        {/* Show "No Data" badge only for machines without data when showing all machines */}
        {showOnlyWithData && !machine.datenVerfuegbar && (
          <div className="flex items-center justify-between">
            <span className={cx(themeClasses.textSecondary, "font-medium")}>Data Status:</span>
            <span className={cx(
              'px-2 py-1 rounded text-xs font-medium border',
              themeClasses.bgErrorSubtle, themeClasses.textError, themeClasses.borderSecondary
            )}>
              {translateStatus('Nicht verfügbar')}
            </span>
          </div>
        )}
      </div>
    </div>
  );
});

// Add display name for debugging
MachineCard.displayName = 'MachineCard';

export default MachineCard;
